import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import config from '../config';
import { fetchWithUserAuth, isJwtExpired, setUserUnauthorizedHandler } from '../utils/userApi';

const UserAuthContext = createContext();

export const useUserAuth = () => {
  const context = useContext(UserAuthContext);
  if (!context) {
    throw new Error('useUserAuth must be used within UserAuthProvider');
  }
  return context;
};

const clearStoredSession = () => {
  localStorage.removeItem('userToken');
  localStorage.removeItem('userData');
};

export const UserAuthProvider = ({ children }) => {
  const [userToken, setUserToken] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    clearStoredSession();
    setUserToken(null);
    setUserData(null);
    setIsAuthenticated(false);
  }, []);

  useEffect(() => {
    setUserUnauthorizedHandler(logout);
    return () => setUserUnauthorizedHandler(null);
  }, [logout]);

  useEffect(() => {
    const bootstrapSession = async () => {
      try {
        const storedToken = localStorage.getItem('userToken');
        const storedUserData = localStorage.getItem('userData');

        if (!storedToken || !storedUserData) {
          clearStoredSession();
          setUserToken(null);
          setUserData(null);
          setIsAuthenticated(false);
          return;
        }

        let parsedUserData;
        try {
          parsedUserData = JSON.parse(storedUserData);
        } catch (parseError) {
          console.error('Corrupt userData in localStorage:', parseError);
          logout();
          return;
        }

        if (isJwtExpired(storedToken)) {
          logout();
          return;
        }

        setUserToken(storedToken);
        setUserData(parsedUserData);

        const apiUrl = config.getApiUrl();
        const data = await fetchWithUserAuth(`${apiUrl}/api/user-auth/me`, {
          method: 'GET',
          token: storedToken
        });

        if (data?.user) {
          localStorage.setItem('userData', JSON.stringify(data.user));
          setUserData(data.user);
        }
        setIsAuthenticated(true);
      } catch (error) {
        if (error.status === 401 || error.status === 404) {
          logout();
          return;
        }
        // Network / 5xx: keep a non-expired stored session so an outage is not a logout
        const storedToken = localStorage.getItem('userToken');
        if (storedToken && !isJwtExpired(storedToken)) {
          setIsAuthenticated(true);
        } else {
          logout();
        }
      } finally {
        setLoading(false);
      }
    };

    bootstrapSession();
  }, [logout]);

  /**
   * Request OTP for phone number or email
   */
  const requestOTP = async (identifier) => {
    try {
      const apiUrl = config.getApiUrl();
      const response = await fetch(`${apiUrl}/api/user-auth/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send OTP');
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error requesting OTP:', error);
      return { success: false, message: error.message };
    }
  };

  /**
   * Verify OTP and login
   */
  const verifyOTP = async (identifier, otp) => {
    try {
      const apiUrl = config.getApiUrl();
      const response = await fetch(`${apiUrl}/api/user-auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier, otp }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to verify OTP');
      }

      localStorage.setItem('userToken', data.token);
      localStorage.setItem('userData', JSON.stringify(data.user));

      setUserToken(data.token);
      setUserData(data.user);
      setIsAuthenticated(true);

      return { success: true, data };
    } catch (error) {
      console.error('Error verifying OTP:', error);
      return { success: false, message: error.message };
    }
  };

  /**
   * Refresh token
   */
  const refreshToken = async () => {
    try {
      if (!userToken) {
        throw new Error('No token available');
      }

      const apiUrl = config.getApiUrl();
      const data = await fetchWithUserAuth(`${apiUrl}/api/user-auth/refresh-token`, {
        method: 'POST',
        token: userToken
      });

      localStorage.setItem('userToken', data.token);
      localStorage.setItem('userData', JSON.stringify(data.user));

      setUserToken(data.token);
      setUserData(data.user);

      return { success: true, data };
    } catch (error) {
      console.error('Error refreshing token:', error);
      logout();
      return { success: false, message: error.message };
    }
  };

  /**
   * Refresh user data from /me endpoint
   * This fetches the latest user data without changing the token
   */
  const refreshUserData = async () => {
    try {
      if (!userToken) {
        throw new Error('No token available');
      }

      const apiUrl = config.getApiUrl();
      const data = await fetchWithUserAuth(`${apiUrl}/api/user-auth/me`, {
        method: 'GET',
        token: userToken
      });

      localStorage.setItem('userData', JSON.stringify(data.user));
      setUserData(data.user);

      return { success: true, data: data.user };
    } catch (error) {
      console.error('Error refreshing user data:', error);
      if (error.status === 401 || error.status === 404) {
        logout();
      }
      return { success: false, message: error.message };
    }
  };

  const value = {
    userToken,
    userData,
    isAuthenticated,
    loading,
    requestOTP,
    verifyOTP,
    logout,
    refreshToken,
    refreshUserData,
  };

  return (
    <UserAuthContext.Provider value={value}>
      {children}
    </UserAuthContext.Provider>
  );
};
